
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BrainCircuit, Lightbulb, GitBranch, PanelLeft, Undo, Redo, Copy, Scissors } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import MindMapCanvas from "./mindmap/MindMapCanvas";
import { useActivityLogger } from "@/hooks/use-activity-logger";

const IdeationTools = () => {
  const [activeTab, setActiveTab] = useState("mindmap");
  const { logActivity } = useActivityLogger();

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    logActivity({
      action: 'view',
      resourceType: 'system',
      details: { page: 'ideation', tab: value }
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Ideation Tools</h2>
          <p className="text-muted-foreground">
            Visualize and organize your thoughts with our interactive ideation tools
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
        <TabsList>
          <TabsTrigger value="mindmap" className="flex items-center gap-2">
            <BrainCircuit className="h-4 w-4" />
            Mind Map
          </TabsTrigger>
          <TabsTrigger value="brainstorm" className="flex items-center gap-2">
            <Lightbulb className="h-4 w-4" />
            Brainstorm
          </TabsTrigger>
          <TabsTrigger value="concepts" className="flex items-center gap-2">
            <GitBranch className="h-4 w-4" />
            Concept Map
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="mindmap" className="space-y-4">
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost">
              <Undo className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="ghost">
              <Redo className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="ghost">
              <Copy className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="ghost">
              <Scissors className="h-4 w-4" />
            </Button>
          </div>
          
          <MindMapCanvas />
        </TabsContent>
        
        <TabsContent value="brainstorm" className="space-y-4">
          <Card className="min-h-[500px] relative">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center p-8 max-w-md">
                <Lightbulb className="h-12 w-12 mx-auto mb-4 text-primary opacity-50" />
                <h3 className="text-xl font-medium mb-2">Brainstorming Board</h3>
                <p className="text-muted-foreground mb-4">
                  A collaborative space for rapid idea generation. This feature is coming soon!
                </p>
              </div>
            </div>
          </Card>
        </TabsContent>
        
        <TabsContent value="concepts" className="space-y-4">
          <Card className="min-h-[500px] relative">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center p-8 max-w-md">
                <GitBranch className="h-12 w-12 mx-auto mb-4 text-primary opacity-50" />
                <h3 className="text-xl font-medium mb-2">Concept Mapping</h3>
                <p className="text-muted-foreground mb-4">
                  Visualize relationships between concepts. This feature is coming soon!
                </p>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default IdeationTools;
